import { useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine
} from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
        <p className="font-semibold text-foreground">{data.name}</p>
        <p className="text-sm text-muted-foreground">Price: ${data.price}</p>
        <p className="text-sm text-accent">ROI: {data.ev_percent.toFixed(1)}%</p>
        <p className="text-sm text-muted-foreground">
          {data.jackpot_available ? '🏆 Jackpot Available' : 'No Jackpot'}
        </p>
      </div>
    );
  }
  return null;
};

export const EVScatterChart = ({ games, onSelectGame }) => {
  const chartData = useMemo(() => {
    return games.map(game => ({
      ...game,
      x: game.price,
      y: game.ev_percent || 0
    }));
  }, [games]);

  const avgEV = useMemo(() => {
    if (games.length === 0) return 0;
    return games.reduce((sum, g) => sum + (g.ev_percent || 0), 0) / games.length;
  }, [games]);

  const getColor = (game) => {
    if (game.ev_percent >= 50) return 'hsl(var(--success))';
    if (game.ev_percent >= 35) return 'hsl(var(--accent))';
    if (game.ev_percent >= 25) return 'hsl(var(--warning))';
    return 'hsl(var(--muted-foreground))';
  };

  return (
    <Card className="col-span-full lg:col-span-2" data-testid="ev-scatter-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-accent"></span>
          EV% vs Price Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
              <XAxis 
                type="number" 
                dataKey="x" 
                name="Price" 
                unit="$"
                domain={[0, 35]}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                label={{ value: 'Ticket Price ($)', position: 'bottom', fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
              />
              <YAxis 
                type="number" 
                dataKey="y" 
                name="ROI" 
                unit="%"
                domain={[0, 80]}
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                label={{ value: 'ROI (%)', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <ReferenceLine 
                y={avgEV} 
                stroke="hsl(var(--accent))" 
                strokeDasharray="5 5" 
                label={{ value: `Avg: ${avgEV.toFixed(1)}%`, fill: 'hsl(var(--accent))', fontSize: 11 }}
              />
              <Scatter 
                data={chartData} 
                cursor="pointer"
                onClick={(data) => onSelectGame && onSelectGame(data.game_id)}
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getColor(entry)}
                    fillOpacity={0.8}
                    r={entry.jackpot_available ? 8 : 5}
                  />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <div className="flex justify-center gap-4 mt-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-success"></span> 50%+ ROI
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-accent"></span> 35-50% ROI
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-warning"></span> 25-35% ROI
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-muted-foreground"></span> &lt;25% ROI
          </span>
        </div>
      </CardContent>
    </Card>
  );
};
